import React, { useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, SignInButton as ClerkSignInButton } from "@clerk/clerk-react";
import { Monitor, Laptop, Check } from "lucide-react";

/**
 * InteractiveHandoffDemo Component
 *
 * Simulates the core MultiKeys software-KVM cursor crossover.
 * Tracks client mouse movements inside a mock environment and shifts active focus 
 * between Windows and macOS systems. Renders a custom glowing cursor.
 */
const InteractiveHandoffDemo: React.FC = () => {
  const [activeScreen, setActiveScreen] = useState<"left" | "right" | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePos({ x, y });
    
    // Divide container down the middle
    const dividerX = rect.width / 2;
    if (x < dividerX) {
      setActiveScreen("left");
    } else {
      setActiveScreen("right");
    }
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setActiveScreen(null);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width: "100%",
        maxWidth: "760px",
        height: "280px",
        backgroundColor: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius-lg)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        padding: "1.5rem",
        marginTop: "2.5rem",
        cursor: "none", // Hide default cursor to render custom glow pointer
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
      }}
      data-testid="interactive-demo-container"
    >
      {/* Dynamic drifting glow behind active monitor */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: activeScreen === "left" ? "25%" : activeScreen === "right" ? "75%" : "50%",
          transform: "translate(-50%, -50%)",
          width: "260px",
          height: "260px",
          borderRadius: "50%",
          background: "radial-gradient(circle, var(--color-accent-glow) 0%, rgba(0,0,0,0) 70%)",
          pointerEvents: "none",
          transition: "left 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
          zIndex: 0,
        }}
      />

      {/* Screen 1: Windows Workstation (Left) */}
      <div
        style={{
          width: "250px",
          height: "165px",
          backgroundColor: "#0B0F19",
          border: activeScreen === "left" ? "2px solid var(--color-accent)" : "2px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          transition: "all 0.3s ease",
          boxShadow: activeScreen === "left" ? "0 0 30px rgba(0, 212, 255, 0.12)" : "none",
          overflow: "hidden",
        }}
      >
        {/* Title bar */}
        <div style={{
          height: "28px",
          backgroundColor: "#161D30",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          alignItems: "center",
          padding: "0 0.75rem",
          justifyContent: "space-between"
        }}>
          <div style={{ display: "flex", gap: "4px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#EF4444" }}></span>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#F59E0B" }}></span>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10B981" }}></span>
          </div>
          <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", fontWeight: 500 }}>
            win11-host
          </span>
        </div>
        {/* Screen body */}
        <div style={{ flex: 1, padding: "0.85rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Monitor size={14} color="var(--color-text-secondary)" />
            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", fontWeight: 600 }}>Windows PC</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "0.2rem" }}>
            <div style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: activeScreen === "left" ? "var(--color-success)" : "var(--color-text-muted)",
              boxShadow: activeScreen === "left" ? "0 0 8px var(--color-success)" : "none",
              transition: "all 0.3s ease"
            }} />
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
              {activeScreen === "left" ? "CONSOLE ACTIVE" : "STANDBY"}
            </span>
          </div>
          <div style={{
            marginTop: "auto",
            height: "32px",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px dashed var(--color-border)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.65rem",
            color: "var(--color-text-muted)"
          }}>
            Move cursor right to cross ➔
          </div>
        </div>
      </div>

      {/* Screen Boundary Visualizer in Center */}
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "2px",
          borderLeft: "2px dashed var(--color-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <div
          style={{
            position: "absolute",
            backgroundColor: activeScreen ? "var(--color-accent)" : "var(--color-border)",
            color: activeScreen ? "var(--color-bg-primary)" : "var(--color-text-muted)",
            fontSize: "0.6rem",
            fontWeight: 800,
            padding: "0.2rem 0.5rem",
            borderRadius: "10px",
            whiteSpace: "nowrap",
            boxShadow: activeScreen ? "0 0 12px var(--color-accent)" : "none",
            transition: "all 0.3s ease",
            transform: "translateY(-65px)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.5px"
          }}
        >
          {activeScreen ? "CURSOR GLIDE OK" : "EDGE BOUNDARY"}
        </div>
      </div>

      {/* Screen 2: macOS Laptop (Right) */}
      <div
        style={{
          width: "250px",
          height: "165px",
          backgroundColor: "#0B0F19",
          border: activeScreen === "right" ? "2px solid var(--color-accent)" : "2px solid var(--color-border)",
          borderRadius: "var(--border-radius-md)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          transition: "all 0.3s ease",
          boxShadow: activeScreen === "right" ? "0 0 30px rgba(0, 212, 255, 0.12)" : "none",
          overflow: "hidden",
        }}
      >
        {/* Title bar */}
        <div style={{
          height: "28px",
          backgroundColor: "#161D30",
          borderBottom: "1px solid var(--color-border)",
          display: "flex",
          alignItems: "center",
          padding: "0 0.75rem",
          justifyContent: "space-between"
        }}>
          <div style={{ display: "flex", gap: "4px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#EF4444" }}></span>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#F59E0B" }}></span>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10B981" }}></span>
          </div>
          <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)", fontWeight: 500 }}>
            mac-client
          </span>
        </div>
        {/* Screen body */}
        <div style={{ flex: 1, padding: "0.85rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Laptop size={14} color="var(--color-text-secondary)" />
            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", fontWeight: 600 }}>MacBook Pro</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "0.2rem" }}>
            <div style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: activeScreen === "right" ? "var(--color-success)" : "var(--color-text-muted)",
              boxShadow: activeScreen === "right" ? "0 0 8px var(--color-success)" : "none",
              transition: "all 0.3s ease"
            }} />
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
              {activeScreen === "right" ? "CONSOLE ACTIVE" : "STANDBY"}
            </span>
          </div>
          <div style={{
            marginTop: "auto",
            height: "32px",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px dashed var(--color-border)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.65rem",
            color: "var(--color-text-muted)"
          }}>
            ◀ Move cursor left to cross
          </div>
        </div>
      </div>

      {/* Hover prompt tooltips */}
      {!isHovered && (
        <div
          style={{
            position: "absolute",
            backgroundColor: "rgba(11, 15, 25, 0.9)",
            backdropFilter: "blur(6px)",
            padding: "0.65rem 1.25rem",
            borderRadius: "var(--border-radius-md)",
            border: "1px solid var(--color-accent)",
            color: "var(--color-text-primary)",
            fontSize: "0.85rem",
            fontWeight: 700,
            textAlign: "center",
            animation: "pulse 2s infinite",
            zIndex: 10,
            pointerEvents: "none",
            boxShadow: "0 0 20px var(--color-accent-glow)"
          }}
        >
          Hover mouse here & slide ⟷ across edges
        </div>
      )}

      {/* Render the custom glow cursor trailing mouse position */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            left: `${mousePos.x}px`,
            top: `${mousePos.y}px`,
            width: "24px",
            height: "24px",
            pointerEvents: "none",
            transform: "translate(-1px, -1px)",
            zIndex: 100,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 0 4px var(--color-accent))" }}>
            <path d="M4.5 3V17.25L8.75 13L11.5 19L13.75 18L11 12H16.5L4.5 3Z" fill="var(--color-accent)" stroke="var(--color-bg-primary)" strokeWidth="1.5" />
          </svg>
          {/* Cyan blur ring under pointer */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "var(--color-accent)",
            opacity: 0.35,
            filter: "blur(3px)"
          }} />
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(1.03); opacity: 1; border-color: var(--color-accent-hover); }
          100% { transform: scale(1); opacity: 0.95; }
        }
      `}</style>
    </div>
  );
};

/**
 * LandingPage Component
 *
 * Handles public-facing landing and product information for MultiKey.dev.
 */
export const LandingPage: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();

  // --- PRICING CONSTANTS (PRD §12) ---
  const pricingTiers = [
    {
      name: "Free Tier",
      price: "$0",
      period: "forever",
      description: "Perfect for secondary laptops or temporary desks.",
      features: [
        "Up to 2 connected computers",
        "Basic horizontal layouts",
        "Instant border crossover",
        "Standard mouse pointer tracking",
      ],
      ctaText: "Get Started",
      isPopular: false,
      priceId: null,
    },
    {
      name: "Pro Plan",
      price: "$4",
      period: "month",
      yearlyOption: "$29/year (~$2.41/mo)",
      description: "Designed for power users with multi-monitor workspaces.",
      features: [
        "Unlimited connected computers",
        "Advanced custom grid layouts",
        "Shared clipboard (text & copy-paste)",
        "Premium neon cursor glow visual effects",
        "14-day free trial (no card required)",
      ],
      ctaText: "Start 14-Day Trial",
      isPopular: true,
      priceId: "price_1Tikke2d2r6FLGvQcDXdGm4T",
    },
    {
      name: "Team Plan",
      price: "$49",
      period: "seat / year",
      description: "For collaborative offices and dev teams sharing control.",
      features: [
        "Everything in Pro",
        "Centralized team seat billing",
        "Shared team layouts & configurations",
        "Priority VIP support ticketing",
        "License key volume controls",
      ],
      ctaText: "Purchase Seats",
      isPopular: false,
      priceId: "price_1Tikkf2d2r6FLGvQ0ByXhzPK",
    },
  ];

  // If already authenticated, redirect automatically to their private dashboard.
  if (isLoaded && isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div 
      style={{ 
        backgroundColor: "var(--color-bg-primary)", 
        color: "var(--color-text-primary)", 
        display: "flex", 
        flexDirection: "column",
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden"
      }}
      data-testid="landing-page"
    >
      {/* Dynamically import Outfit Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
        .outfit-font {
          font-family: 'Outfit', sans-serif !important;
        }
      `}</style>

      {/* Background noise and grain overlay */}
      <div 
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.015'/%3E%3C/svg%3E")`,
          pointerEvents: "none",
          zIndex: 1
        }}
      />

      {/* Drifting ambient neon blur mesh */}
      <div
        style={{
          position: "absolute",
          top: "-150px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "900px",
          height: "500px",
          background: "radial-gradient(ellipse at center, rgba(0, 212, 255, 0.07) 0%, rgba(11, 15, 25, 0) 75%)",
          pointerEvents: "none",
          zIndex: 1
        }}
      />

      {/* 1. HERO SECTION */}
      <section 
        style={{ 
          padding: "6rem 1.5rem 4rem", 
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1.5rem",
          position: "relative",
          zIndex: 2
        }}
        data-testid="landing-hero"
      >
        <span 
          style={{
            backgroundColor: "var(--color-accent-glow)",
            border: "1px solid var(--color-accent)",
            color: "var(--color-accent)",
            padding: "0.3rem 0.85rem",
            borderRadius: "20px",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.8px",
            textTransform: "uppercase"
          }}
          data-testid="hero-badge"
        >
          Beta Feasibility Release
        </span>
        
        <h1 
          className="outfit-font"
          style={{ 
            fontSize: "3.75rem", 
            lineHeight: "1.1", 
            fontWeight: 800,
            maxWidth: "850px",
            margin: "0 auto",
            letterSpacing: "-0.5px"
          }}
        >
          One Keyboard. One Mouse. <br />
          <span style={{ color: "var(--color-accent)" }}>All Your Computers.</span>
        </h1>
        
        <p 
          style={{ 
            color: "var(--color-text-secondary)", 
            fontSize: "1.2rem", 
            maxWidth: "600px", 
            margin: "0.25rem auto 0.75rem",
            lineHeight: "1.6"
          }}
        >
          Seamlessly share controls across Mac, Windows, and Linux. Move your mouse off the edge of one screen to control the next.
        </p>

        {/* Action CTAs */}
        <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", justifyContent: "center", zIndex: 10 }}>
          <ClerkSignInButton mode="modal">
            <button
              style={{
                backgroundColor: "var(--color-accent)",
                color: "var(--color-bg-primary)",
                border: "none",
                borderRadius: "var(--border-radius-md)",
                padding: "0.9rem 2rem",
                fontSize: "1rem",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 0 20px rgba(0, 212, 255, 0.25)",
                transition: "all var(--transition-speed) ease"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent-hover)";
                e.currentTarget.style.boxShadow = "0 0 25px var(--color-accent)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent)";
                e.currentTarget.style.boxShadow = "0 0 20px rgba(0, 212, 255, 0.25)";
              }}
              data-testid="hero-cta-signin"
            >
              Get Started for Free
            </button>
          </ClerkSignInButton>

          <Link
            to="/download"
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-md)",
              padding: "0.9rem 2rem",
              fontSize: "1rem",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              transition: "all var(--transition-speed) ease"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "var(--color-accent)";
              e.currentTarget.style.backgroundColor = "rgba(0, 212, 255, 0.02)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.backgroundColor = "var(--color-bg-secondary)";
            }}
            data-testid="hero-cta-download"
          >
            Download Client
          </Link>
        </div>

        {/* Live Interactive Handoff Mockup Screen */}
        <InteractiveHandoffDemo />
      </section>

      {/* 2. FEATURES GRID SECTION */}
      <section 
        style={{ 
          padding: "5rem 1.5rem", 
          borderTop: "1px solid var(--color-border)",
          backgroundColor: "var(--color-bg-secondary)",
          position: "relative",
          zIndex: 2
        }}
        data-testid="landing-features"
      >
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 
            className="outfit-font"
            style={{ textAlign: "center", fontSize: "2.25rem", marginBottom: "3.5rem", fontWeight: 700 }}
          >
            Why Choose MultiKeys?
          </h2>
          <div 
            style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
              gap: "3rem" 
            }}
          >
            {/* Feature 1 */}
            <div 
              style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "0.75rem",
                padding: "1.5rem",
                backgroundColor: "var(--color-bg-primary)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--color-border)",
                transition: "border-color 0.3s ease"
              }} 
              onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--color-accent)"}
              onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
              data-testid="feature-card-layout"
            >
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "var(--border-radius-md)",
                backgroundColor: "var(--color-accent-glow)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                color: "var(--color-accent)"
              }}>◫</div>
              <h3 className="outfit-font" style={{ fontSize: "1.3rem", fontWeight: 600 }}>Intuitive Canvas Layouts</h3>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: "1.6" }}>
                Drag and drop your computer screens onto our config canvas matching your physical desk configuration. Handles non-aligned edges and monitor rotations easily.
              </p>
            </div>
            {/* Feature 2 */}
            <div 
              style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "0.75rem",
                padding: "1.5rem",
                backgroundColor: "var(--color-bg-primary)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--color-border)",
                transition: "border-color 0.3s ease"
              }} 
              onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--color-accent)"}
              onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
              data-testid="feature-card-latency"
            >
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "var(--border-radius-md)",
                backgroundColor: "var(--color-accent-glow)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                color: "var(--color-accent)"
              }}>⚡</div>
              <h3 className="outfit-font" style={{ fontSize: "1.3rem", fontWeight: 600 }}>Ultra-Low Latency</h3>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: "1.6" }}>
                Engineered with high-frequency network packets. Switch controls and drag inputs instantly without stutter or lag.
              </p>
            </div>
            {/* Feature 3 */}
            <div 
              style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "0.75rem",
                padding: "1.5rem",
                backgroundColor: "var(--color-bg-primary)",
                borderRadius: "var(--border-radius-lg)",
                border: "1px solid var(--color-border)",
                transition: "border-color 0.3s ease"
              }} 
              onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--color-accent)"}
              onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
              data-testid="feature-card-clipboard"
            >
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "var(--border-radius-md)",
                backgroundColor: "var(--color-accent-glow)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                color: "var(--color-accent)"
              }}>📋</div>
              <h3 className="outfit-font" style={{ fontSize: "1.3rem", fontWeight: 600 }}>Shared Clipboard</h3>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: "1.6" }}>
                Copy text or images on one machine and paste them immediately on another. Fully integrated securely across your private LAN.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PRICING MATRIX SECTION */}
      <section 
        style={{ padding: "6rem 1.5rem", position: "relative", zIndex: 2 }}
        data-testid="landing-pricing"
      >
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 
            className="outfit-font"
            style={{ textAlign: "center", fontSize: "2.25rem", marginBottom: "0.5rem", fontWeight: 700 }}
          >
            Simple, Transparent Pricing
          </h2>
          <p style={{ textAlign: "center", color: "var(--color-text-secondary)", marginBottom: "4rem", fontSize: "1.1rem" }}>
            Start with our 14-day free Pro trial. No credit card required.
          </p>

          <div 
            style={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "stretch", 
              gap: "2.5rem", 
              flexWrap: "wrap" 
            }}
          >
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: tier.isPopular ? "2px solid var(--color-accent)" : "1px solid var(--color-border)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "3rem 2.25rem",
                  flex: "1 1 300px",
                  maxWidth: "340px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                  position: "relative",
                  boxShadow: tier.isPopular ? "0 0 30px var(--color-accent-glow)" : "0 4px 20px rgba(0, 0, 0, 0.15)",
                  transition: "transform 0.3s ease"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "none";
                }}
                data-testid={`pricing-card-${tier.name.toLowerCase().split(" ")[0]}`}
              >
                {tier.isPopular && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-13px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "var(--color-accent)",
                      color: "var(--color-bg-primary)",
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      padding: "0.3rem 1rem",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                    data-testid="plan-popular-badge"
                  >
                    Recommended
                  </span>
                )}
                
                <div>
                  <h3 className="outfit-font" style={{ fontSize: "1.6rem", fontWeight: 700 }}>{tier.name}</h3>
                  <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", marginTop: "0.4rem", lineHeight: "1.5" }}>
                    {tier.description}
                  </p>
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
                  <span style={{ fontSize: "3.25rem", fontWeight: 800, color: "var(--color-text-primary)" }}>
                    {tier.price}
                  </span>
                  <span style={{ color: "var(--color-text-muted)", fontSize: "1rem" }}>
                    / {tier.period}
                  </span>
                </div>
                
                {tier.yearlyOption && (
                  <div 
                    style={{ color: "var(--color-success)", fontSize: "0.85rem", fontWeight: 700, marginTop: "-1rem" }}
                    data-testid="pricing-yearly-option"
                  >
                    {tier.yearlyOption}
                  </div>
                )}

                {/* Features Checklist */}
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "0.85rem", margin: "0.75rem 0" }}>
                  {tier.features.map((feature, fIdx) => (
                    <li 
                      key={fIdx} 
                      style={{ 
                        fontSize: "0.9rem", 
                        color: "var(--color-text-secondary)",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.6rem",
                        lineHeight: "1.4"
                      }}
                    >
                      <Check size={16} color="var(--color-success)" style={{ marginTop: "2px", flexShrink: 0 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Pricing card action CTA */}
                <div style={{ marginTop: "auto", zIndex: 10 }}>
                  <ClerkSignInButton mode="modal">
                    <button
                      style={{
                        width: "100%",
                        backgroundColor: tier.isPopular ? "var(--color-accent)" : "transparent",
                        color: tier.isPopular ? "var(--color-bg-primary)" : "var(--color-accent)",
                        border: "1px solid var(--color-accent)",
                        borderRadius: "var(--border-radius-md)",
                        padding: "0.8rem 1.5rem",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all var(--transition-speed) ease"
                      }}
                      onMouseOver={(e) => {
                        if (!tier.isPopular) {
                          e.currentTarget.style.backgroundColor = "var(--color-accent-glow)";
                        } else {
                          e.currentTarget.style.backgroundColor = "var(--color-accent-hover)";
                          e.currentTarget.style.boxShadow = "0 0 15px var(--color-accent)";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!tier.isPopular) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        } else {
                          e.currentTarget.style.backgroundColor = "var(--color-accent)";
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }}
                    >
                      {tier.ctaText}
                    </button>
                  </ClerkSignInButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. DOWNLOAD CALL TO ACTION */}
      <section 
        style={{ 
          padding: "5rem 1.5rem",
          backgroundColor: "var(--color-bg-secondary)",
          borderTop: "1px solid var(--color-border)",
          textAlign: "center",
          position: "relative",
          zIndex: 2
        }}
        data-testid="landing-download"
      >
        <div style={{ maxWidth: "650px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "1.75rem", alignItems: "center" }}>
          <h2 className="outfit-font" style={{ fontSize: "2.25rem", fontWeight: 700 }}>Ready to pair your machines?</h2>
          
          <p style={{ color: "var(--color-text-secondary)", fontSize: "1.1rem", lineHeight: "1.6" }}>
            Get the client desktop agent. Runs natively on Windows 10/11, macOS Monterey or newer, and modern Linux distros (X11 & Wayland).
          </p>
          
          <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", justifyContent: "center" }}>
            <Link 
              to="/download" 
              style={{
                backgroundColor: "var(--color-accent)",
                color: "var(--color-bg-primary)",
                padding: "0.85rem 1.75rem",
                borderRadius: "var(--border-radius-md)",
                fontWeight: 700,
                boxShadow: "0 0 15px rgba(0, 212, 255, 0.2)"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent-hover)";
                e.currentTarget.style.boxShadow = "0 0 20px var(--color-accent)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent)";
                e.currentTarget.style.boxShadow = "0 0 15px rgba(0, 212, 255, 0.2)";
              }}
              data-testid="download-windows"
            >
              Get for Windows (x64)
            </Link>
            
            <Link 
              to="/download" 
              style={{
                backgroundColor: "var(--color-bg-primary)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border)",
                padding: "0.85rem 1.75rem",
                borderRadius: "var(--border-radius-md)",
                fontWeight: 600,
                transition: "border-color 0.2s"
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--color-accent)"}
              onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
              data-testid="download-mac"
            >
              Get for macOS (Apple Silicon)
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
